import React from 'react';

import { Box, ButtonGroup, Flex, forwardRef, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { ChevronDown } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import GradientButton from 'uiToolkitV1/GradientButton/GradientButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const BUTTON_VARIANT = {
  filled: 'filled',
  outlined: 'outlined',
  tertiary: 'tertiary',
};

const StyledMenuItem = styled(MenuItem)`
  padding: 8px;
  border-radius: 4px;
  margin-top: 4px;
  &:hover&:enabled,
  &:focus&:enabled {
    background: #cae8f0;
    p {
      color: #004f67;
    }
    svg {
      color: #004f67;
    }
  }
`;

const StyledButton = styled(Button)`
  &:active:enabled,
  &:focus:enabled {
    background-color: #e6f1fd;
  }
`;

const StyledButtonGroup = styled(ButtonGroup)`
  // & div[data-popper-placement] {
  //   transform: none !important;
  //   top: 110% !important;
  //   left: 0px !important;
  //   width: 100% !important;
  // }
  > *:first-of-type:not(:last-of-type) {
    button {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
  }
  // >*:not(:first-of-type):last-of-type
  > *:nth-of-type(2) {
    button {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
  }
`;

const SplitButton = ({
  buttonProps,
  menuButtonProps,
  menuListProps = {},
  options = [],
  defaultIndex = 0,
  indexesToRender,
  menuPlacement = 'bottom-end',
  menuProps,
  StartIcon = null,
  minWidth = 150,
  isUsePortal = false,
  isDisabled = false,
  isGradient = false,
  ...props
}) => {
  const selectedValue = options[defaultIndex];
  const containerRef = React.useRef();

  const isOutlined = isGradient || buttonProps?.variant === BUTTON_VARIANT.outlined;
  const isTertiary = buttonProps?.variant === BUTTON_VARIANT.tertiary;

  const StyledButtonVariant = isOutlined || isTertiary ? StyledButton : Button;

  const DropdownButton = forwardRef((props, ref) => (
    <StyledButtonVariant borderLeft="none !important" isDisabled={isDisabled} ref={ref} {...buttonProps} {...props} />
  ));
  const DropdownGradientButton = forwardRef((props, ref) => (
    <GradientButton
      text=""
      icon={props.StartIcon}
      isDisabled={isDisabled}
      ref={ref}
      buttonProps={{
        className: 'ai-split-button-dropdown',
      }}
      {...props}
    />
  ));

  if (options.length === 1) {
    const { icon = null, value, label, onClick = () => {}, isDisabled = false, tooltipText } = options[0];

    return (
      <Tooltip label={tooltipText}>
        <Box>
          <Button StartIcon={icon} onClick={onClick} isDisabled={isDisabled} variant={buttonProps?.variant ?? 'filled'}>
            {label}
          </Button>
        </Box>
      </Tooltip>
    );
  }

  return (
    <StyledButtonGroup position="relative" isAttached isDisabled={isDisabled} {...props}>
      <Menu placement={menuPlacement} preventOverflow={false} flip={false} gutter={0} {...menuProps}>
        {({ isOpen }) => (
          <>
            <Tooltip label={selectedValue?.tooltipText ?? ''}>
              <Box>
                {isGradient ? (
                  <GradientButton
                    // @ts-ignore
                    text={selectedValue?.label}
                    icon={selectedValue?.icon ?? StartIcon}
                    buttonProps={{
                      className: 'ai-split-button',
                    }}
                    onClick={selectedValue?.onClick}
                    isDisabled={(isDisabled || selectedValue?.isDisabled) ?? false}
                    ref={containerRef}
                    p={0}
                  />
                ) : (
                  <StyledButtonVariant
                    padding="8px 6px !important"
                    borderTopRightRadius={0}
                    borderBottomRightRadius={0}
                    isDisabled={selectedValue?.isDisabled ?? false}
                    borderRight={`1px solid ${isDisabled ? '#5d6dbe1a' : '#125AA9'} !important`}
                    {...buttonProps}
                    onClick={selectedValue?.onClick}
                    StartIcon={selectedValue?.icon ?? StartIcon}
                    ref={containerRef}
                  >
                    {selectedValue?.label}
                  </StyledButtonVariant>
                )}
              </Box>
            </Tooltip>
            <div>
              <MenuButton
                sx={{
                  p: {
                    margin: '0',
                  },
                  svg: {
                    transition: 'transform 150ms ease-in-out',
                  },
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,

                  '&[aria-expanded="true"]': {
                    background: isOutlined ? 'transparent' : isTertiary ? '#E6F1FD' : '#004F67 !important',
                    svg: {
                      transform: 'rotate(180deg)',
                    },
                  },
                }}
                as={isGradient ? DropdownGradientButton : DropdownButton}
                StartIcon={ChevronDown}
                {...menuButtonProps}
              />
            </div>

            {isUsePortal ? (
              <Portal>
                <MenuList
                  sx={{
                    width: '100%',
                    boxShadow: '2px 0px 4px 3px rgba(0, 0, 0, 0.12), 0px 4px 4px 3px rgba(0, 0, 0, 0.08)',
                    borderRadius: '8px',
                    padding: '8px',
                    minWidth,
                  }}
                  {...menuListProps}
                >
                  {options?.map(
                    (
                      { icon = null, value, label, onClick = () => {}, isDisabled = false, tooltipText = '' },
                      index
                    ) => {
                      const IconComp = icon;
                      if (Array.isArray(indexesToRender)) {
                        if (indexesToRender.includes(index)) {
                          return (
                            <Tooltip label={tooltipText}>
                              <Box>
                                <StyledMenuItem
                                  onClick={() => {
                                    onClick({ value });
                                  }}
                                  key={`${value}-${index}`}
                                  isDisabled={isDisabled}
                                >
                                  <Flex alignItems="center" w="100%">
                                    {IconComp && <IconComp mr="4px" />}
                                    <TextBodyMedium>{label}</TextBodyMedium>
                                    {/* {isSelected && <Check ml="auto" color={'button.blue.focus'} />} */}
                                  </Flex>
                                </StyledMenuItem>
                              </Box>
                            </Tooltip>
                          );
                        }
                        return null;
                      }
                      return (
                        <Tooltip label={tooltipText}>
                          <Box>
                            <StyledMenuItem
                              onClick={() => {
                                onClick({ value });
                              }}
                              key={`${value}-${index}`}
                              isDisabled={isDisabled}
                            >
                              <Flex alignItems="center" w="100%">
                                {IconComp && <IconComp mr="4px" />}
                                <TextBodyMedium>{label}</TextBodyMedium>
                                {/* {isSelected && <Check ml="auto" color={'button.blue.focus'} />} */}
                              </Flex>
                            </StyledMenuItem>
                          </Box>
                        </Tooltip>
                      );
                    }
                  )}
                </MenuList>
              </Portal>
            ) : (
              <MenuList
                sx={{
                  width: '100%',
                  boxShadow: '2px 0px 4px 3px rgba(0, 0, 0, 0.12), 0px 4px 4px 3px rgba(0, 0, 0, 0.08)',
                  borderRadius: '8px',
                  padding: '8px',
                  minWidth,
                }}
                {...menuListProps}
              >
                {options?.map(
                  ({ icon = null, value, label, onClick = () => {}, isDisabled = false, tooltipText = '' }, index) => {
                    const IconComp = icon;
                    if (Array.isArray(indexesToRender)) {
                      if (indexesToRender.includes(index)) {
                        return (
                          <Tooltip label={tooltipText}>
                            <Box>
                              <StyledMenuItem
                                onClick={() => {
                                  onClick({ value });
                                }}
                                key={`${value}-${index}`}
                                isDisabled={isDisabled}
                              >
                                <Flex alignItems="center" w="100%">
                                  {IconComp && <IconComp mr="4px" boxSize="20px" />}
                                  <TextBodyMedium>{label}</TextBodyMedium>
                                  {/* {isSelected && <Check ml="auto" color={'button.blue.focus'} />} */}
                                </Flex>
                              </StyledMenuItem>
                            </Box>
                          </Tooltip>
                        );
                      }
                      return null;
                    }
                    return (
                      <Tooltip label={tooltipText}>
                        <Box>
                          <StyledMenuItem
                            onClick={() => {
                              onClick({ value });
                            }}
                            key={`${value}-${index}`}
                            isDisabled={isDisabled}
                          >
                            <Flex alignItems="center" w="100%">
                              {IconComp && <IconComp mr="4px" boxSize="20px" />}
                              <TextBodyMedium>{label}</TextBodyMedium>
                              {/* {isSelected && <Check ml="auto" color={'button.blue.focus'} />} */}
                            </Flex>
                          </StyledMenuItem>
                        </Box>
                      </Tooltip>
                    );
                  }
                )}
              </MenuList>
            )}
            {/* <Portal> */}

            {/* </Portal> */}
          </>
        )}
      </Menu>
    </StyledButtonGroup>
  );
};

export { SplitButton, BUTTON_VARIANT };
